package ru.chm.gradereport.model

import kotlinx.serialization.Serializable

@Serializable
enum class UserRole {
    ADMIN,
    REVIEWER,
}

@Serializable
enum class AttendanceStatus {
    PRESENT,
    ABSENT,
    LATE,
    EXCUSED,
    /** Student self check-in; shown purple; counts as unmarked until confirmed. */
    PENDING,
}

@Serializable
data class UserDto(
    val id: String,
    val login: String,
    val role: UserRole,
    val displayName: String,
    val uid: String = "",
)

@Serializable
data class CourseDto(
    val id: String,
    val name: String,
    val description: String,
    val publicToken: String,
    /** Separate token for the public homework-only page (#/hw/{token}). */
    val homeworkToken: String = "",
    val reviewerIds: List<String> = emptyList(),
    val reviewerNames: List<String> = emptyList(),
    val studentCount: Int = 0,
)

@Serializable
data class ReviewerDto(
    val id: String,
    val login: String,
    val displayName: String,
    val uid: String = "",
    val courseIds: List<String> = emptyList(),
    val courseNames: List<String> = emptyList(),
)

@Serializable
data class StudentDto(
    val id: String,
    val courseId: String,
    val fullName: String,
    val studentNumber: String,
    val orderIndex: Int,
    val uid: String = "",
)

@Serializable
data class LessonSessionDto(
    val id: String,
    val courseId: String,
    val date: String,
    val title: String,
    val description: String = "",
)

@Serializable
data class AttendanceDto(
    val studentId: String,
    val sessionId: String,
    val status: AttendanceStatus? = null,
)

@Serializable
data class HomeworkDto(
    val id: String,
    val courseId: String,
    val sessionId: String? = null,
    val title: String,
    val description: String = "",
    val maxScore: Double,
    val orderIndex: Int,
    /** Students who attached at least one submission link (not total links). */
    val submittedCount: Int = 0,
)

@Serializable
data class HomeworkGradeDto(
    val studentId: String,
    val homeworkId: String,
    val score: Double?,
)

/** Public list item for forms: students of a course with uid. */
@Serializable
data class CourseStudentListItem(
    val uid: String,
    val fullName: String,
    val studentNumber: String = "",
)

@Serializable
data class HomeworkSubmissionDto(
    val id: String,
    val studentUid: String,
    val homeworkId: String,
    val url: String,
    val createdAt: Long,
)

/** Submission link inside the grade sheet (student id, not uid). */
@Serializable
data class HomeworkSubmissionItemDto(
    val id: String,
    val studentId: String,
    val homeworkId: String,
    val url: String,
    val createdAt: Long,
    /** True once the answer has been acknowledged (graded / opened). */
    val seen: Boolean = false,
)

@Serializable
data class GradeSheetDto(
    val course: CourseDto,
    val students: List<StudentDto>,
    val sessions: List<LessonSessionDto>,
    val attendance: List<AttendanceDto>,
    val homeworks: List<HomeworkDto>,
    val grades: List<HomeworkGradeDto>,
    val submissions: List<HomeworkSubmissionItemDto> = emptyList(),
)

@Serializable
data class StudentStatsDto(
    val student: StudentDto,
    val courseName: String,
    val attendancePresent: Int,
    val attendanceAbsent: Int,
    val attendanceLate: Int,
    val attendanceExcused: Int,
    val attendanceTotal: Int,
    val attendancePercent: Double,
    val homeworkCompleted: Int,
    val homeworkTotal: Int,
    val homeworkAveragePercent: Double,
    val homeworkScores: List<HomeworkScoreItem>,
    val attendanceItems: List<AttendanceSessionItem> = emptyList(),
)

@Serializable
data class HomeworkScoreItem(
    val homeworkId: String,
    val title: String,
    val score: Double?,
    val maxScore: Double,
    val percent: Double?,
    val description: String = "",
    val sessionId: String? = null,
    val sessionDate: String = "",
    val sessionTitle: String = "",
    val submissionUrls: List<String> = emptyList(),
)

@Serializable
data class AttendanceSessionItem(
    val sessionId: String,
    val date: String,
    val title: String,
    val status: AttendanceStatus? = null,
)

@Serializable
data class CourseAnalyticsDto(
    val courseId: String,
    val totalStudents: Int,
    val averageAttendancePercent: Double,
    val averageHomeworkPercent: Double,
    val topStudents: List<StudentRankItem>,
    val needsSupport: List<StudentRankItem>,
    val attendanceDistribution: Map<String, Int>,
)

@Serializable
data class StudentRankItem(
    val studentId: String,
    val fullName: String,
    val scorePercent: Double,
)

@Serializable
data class CheckInStudentOption(
    val id: String,
    val fullName: String,
)

@Serializable
data class CheckInSessionDto(
    val token: String,
    val courseId: String,
    val sessionId: String,
    val sessionLabel: String,
    val courseName: String,
    val expiresAtEpochMs: Long,
    val checkInPath: String,
    /** PNG QR of absolute check-in URL; may be empty if generation failed. */
    val qrPngBase64: String = "",
)

@Serializable
data class PublicCheckInInfoDto(
    val token: String,
    val courseName: String,
    val sessionLabel: String,
    val expiresAtEpochMs: Long,
    val expired: Boolean,
    val students: List<CheckInStudentOption>,
)
